import React, { useState, useEffect } from "react";
import DotDropdown from "../DotDropdown";
import {
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Grid,
  Typography,
  Select as MuiSelect,
  MenuItem,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import "./../NcdForm.css";

dayjs.extend(customParseFormat);

export const Breastphysical = ({ data, onChange }) => {
  const assesDate = data?.assesDate || data?.assessmentDate || '';
  const noAbnormalityFound = Boolean(data?.noAbnormalityFound);

  const [selectedValue, setSelectedValue] = useState('');
  const [dotData, setDotData] = useState({});
  const [activeDot, setActiveDot] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [hoveredDot, setHoveredDot] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const axillaryOptions = [
    { value: 'Select All', label: 'Select All' },
    { value: 'Normal', label: 'Normal' },
    { value: 'Free', label: 'Free' },
    { value: 'Matted', label: 'Matted' },
    { value: 'Attached to skin', label: 'Attached to skin' },
    { value: 'Single', label: 'Single' },
    { value: 'Multiple', label: 'Multiple' }
  ];

  const supraclavicularOptions = [
    { value: 'All', label: 'Select All' },
    { value: 'Normal', label: 'Normal' },
    { value: 'Free', label: 'Free' },
    { value: 'Matted', label: 'Matted' },
    { value: 'Attached to skin', label: 'Attached to skin' },
    { value: 'Single', label: 'Single' },
    { value: 'Multiple', label: 'Multiple' }
  ];

  const getSymptomType = (val) => {
    if (val === "Lump") return "lump";
    if (val === "Ulcer") return "ulcer";
    if (val === "Retraction of Nipple") return "nippleRetraction";
    if (val === "Nipple Discharge") return "nippleDischarge";
    if (val === "Skin Dimpling Puckering") return "dimpling";
    if (val === "Retraction of Skin") return "retractionSkin";
    return val ? val.toLowerCase() : "";
  };

  const convertDotIdToApiId = (dotId) => {
    if (!dotId) return null;
    if (dotId === "left-center") return "RB13";
    if (dotId === "right-center") return "LB13";
    const isLeft = dotId.startsWith("left");
    const numberMatch = dotId.match(/\d+/);
    const number = numberMatch ? numberMatch[0] : "";
    return `${isLeft ? "RB" : "LB"}${number}`;
  };

  const matchesPointId = (point, targetApiId) => {
    if (!point) return false;
    const pointId = typeof point === 'string' ? point : point.id;
    if (!pointId) return false;

    if (pointId === targetApiId) return true;

    if ((targetApiId === "RB13" || targetApiId === "RB-center") && (pointId === "RB13" || pointId === "RBCenter" || pointId === "RB-center")) return true;
    if ((targetApiId === "LB13" || targetApiId === "LB-center") && (pointId === "LB13" || pointId === "LBCenter" || pointId === "LB-center")) return true;

    return false;
  };

  const getDotFindings = (dotId, selectedVal) => {
    const apiId = convertDotIdToApiId(dotId);
    if (!apiId) return [];

    const examPoints = data?.brstExmLctnsPnts || data?.examLocationPoints || {};

    const symptomCategories = [
      {
        id: "Lump",
        label: "Lump",
        type: "lump",
        keys: ["lumpPoints"]
      },
      {
        id: "Ulcer",
        label: "Ulcer",
        type: "ulcer",
        keys: ["ulcerPoints"]
      },
      {
        id: "Retraction of Nipple",
        label: "Retraction of Nipple",
        type: "nippleRetraction",
        keys: ["nplRtctnPoints", "nippleRetractionPoints"]
      },
      {
        id: "Nipple Discharge",
        label: "Nipple Discharge",
        type: "nippleDischarge",
        keys: ["npldischrgPoints", "nippleDischargePoints"]
      },
      {
        id: "Skin Dimpling Puckering",
        label: "Skin Dimpling / Puckering",
        type: "dimpling",
        keys: ["dmplngPoints", "dimplingPoints"]
      },
      {
        id: "Retraction of Skin",
        label: "Retraction of Skin",
        type: "retractionSkin",
        keys: ["retrctnSknPoints", "retractionSkinPoints"]
      }
    ];

    const findingsMap = new Map();

    symptomCategories.forEach((cat) => {
      cat.keys.forEach((key) => {
        const arr = examPoints[key];
        if (Array.isArray(arr)) {
          const matchPoint = arr.find((p) => matchesPointId(p, apiId));
          if (matchPoint) {
            const attr = typeof matchPoint === 'object' ? matchPoint.attributes || {} : {};
            findingsMap.set(cat.type, {
              id: cat.id,
              label: cat.label,
              type: cat.type,
              attributes: attr
            });
          }
        }
      });
    });

    if (dotData[dotId]) {
      const localDot = dotData[dotId];
      const cat = symptomCategories.find(c => c.type === localDot.type);
      if (cat) {
        findingsMap.set(cat.type, {
          id: cat.id,
          label: cat.label,
          type: cat.type,
          attributes: {
            consistency: localDot.selectedOption1 || localDot.consistency,
            fixity: localDot.selectedOption2 || localDot.fixity,
            size: localDot.textValue || localDot.size
          }
        });
      }
    }

    const allFindings = Array.from(findingsMap.values());

    if (selectedVal) {
      const targetType = getSymptomType(selectedVal);
      return allFindings.filter((f) => f.id === selectedVal || f.type === targetType || f.label === selectedVal);
    }

    return allFindings;
  };

  useEffect(() => {
    const listener = (e) => {
      const { dotId, data: eventData, type } = e.detail;
      const currentSelectedValue = type || selectedValue;
      const symptomType = getSymptomType(currentSelectedValue);

      setDotData((prev) => ({ ...prev, [dotId]: { type: symptomType, ...eventData } }));
      setActiveDot(null);

      const formattedId = convertDotIdToApiId(dotId);
      const currentPoints = data?.brstExmLctnsPnts || data?.examLocationPoints || {};

      const typeMap = {
        "Lump": "lumpPoints",
        "Ulcer": "ulcerPoints",
        "Retraction of Nipple": "nplRtctnPoints",
        "Nipple Discharge": "npldischrgPoints",
        "Skin Dimpling Puckering": "dmplngPoints",
        "Retraction of Skin": "retrctnSknPoints",
      };
      const pointTypeKey = typeMap[currentSelectedValue] || "lumpPoints";
      const currentList = currentPoints[pointTypeKey] || [];

      let attributes = {};
      if (currentSelectedValue === "Lump") {
        attributes = {
          consistency: eventData.selectedOption1,
          fixity: eventData.selectedOption2,
          size: eventData.textValue,
        };
      } else if (currentSelectedValue === "Nipple Discharge") {
        const dType = eventData.Discharge || eventData.dischargeType || eventData.selectedOption1 || "";
        attributes = {
          Discharge: dType,
          OtherDisc: dType === "Other" ? (eventData.OtherDisc || eventData.dischargeOther || eventData.textValue || "") : ""
        };
      } else {
        attributes = eventData;
      }

      const newEntry = currentSelectedValue === "Retraction of Nipple" ? {
        id: formattedId
      } : {
        id: formattedId,
        attributes
      };

      const updatedList = [...currentList.filter(p => !matchesPointId(p, formattedId)), newEntry];

      const altKeyMap = {
        "lumpPoints": "lumpPoints",
        "ulcerPoints": "ulcerPoints",
        "nplRtctnPoints": "nippleRetractionPoints",
        "npldischrgPoints": "nippleDischargePoints",
        "dmplngPoints": "dimplingPoints",
        "retrctnSknPoints": "retractionSkinPoints"
      };
      const altKey = altKeyMap[pointTypeKey];

      const updatedPointsObj = {
        ...currentPoints,
        [pointTypeKey]: updatedList,
        ...(altKey ? { [altKey]: updatedList } : {})
      };

      const hasLump = (updatedPointsObj.lumpPoints?.length > 0) ? true : null;
      const hasUlcer = (updatedPointsObj.ulcerPoints?.length > 0) ? true : null;
      const hasNplRtctn = ((updatedPointsObj.nplRtctnPoints || updatedPointsObj.nippleRetractionPoints)?.length > 0) ? true : null;
      const hasNplDischrg = ((updatedPointsObj.npldischrgPoints || updatedPointsObj.nippleDischargePoints)?.length > 0) ? true : null;
      const hasDmplng = ((updatedPointsObj.dmplngPoints || updatedPointsObj.dimplingPoints)?.length > 0) ? true : null;
      const hasSknRetrctn = ((updatedPointsObj.retrctnSknPoints || updatedPointsObj.retractionSkinPoints)?.length > 0) ? true : null;

      onChange({
        ...data,
        lump: hasLump,
        ulcrtn: hasUlcer,
        niplRetrctn: hasNplRtctn,
        dschrge: hasNplDischrg,
        dmplng: hasDmplng,
        sknRetrctn: hasSknRetrctn,
        brstExmLctnsPnts: updatedPointsObj,
        examLocationPoints: updatedPointsObj
      });
    };

    const handleGlobalClick = () => setActiveDot(null);

    window.addEventListener("dotDataSaved", listener);
    window.addEventListener("click", handleGlobalClick);
    return () => {
      window.removeEventListener("dotDataSaved", listener);
      window.removeEventListener("click", handleGlobalClick);
    };
  }, [selectedValue, data, onChange]);

  const handleDotClick = (dotId, e) => {
    if (noAbnormalityFound) return;
    e.stopPropagation();

    if (!selectedValue) {
      setSelectedValue('Lump');
      return;
    }

    if (selectedValue === "Nipple Discharge" || selectedValue === "Retraction of Nipple") {
      if (dotId !== "left-center" && dotId !== "right-center") {
        return;
      }
    }

    const formattedId = convertDotIdToApiId(dotId);

    const typeMap = {
      "Lump": "lumpPoints",
      "Ulcer": "ulcerPoints",
      "Retraction of Nipple": "nplRtctnPoints",
      "Nipple Discharge": "npldischrgPoints",
      "Skin Dimpling Puckering": "dmplngPoints",
      "Retraction of Skin": "retrctnSknPoints",
    };
    const pointTypeKey = typeMap[selectedValue];
    if (!pointTypeKey) return;

    if (selectedValue === "Lump" || selectedValue === "Nipple Discharge") {
      const rect = e.target.getBoundingClientRect();
      setActiveDot(dotId);
      setDropdownPos({ top: rect.top - 100, left: rect.left - 250 });
    } else {
      const symptomType = getSymptomType(selectedValue);
      const currentFindings = getDotFindings(dotId, selectedValue);
      const alreadySelected = currentFindings.length > 0;

      const currentPoints = data?.brstExmLctnsPnts || data?.examLocationPoints || {};
      const currentList = currentPoints[pointTypeKey] || currentPoints[symptomType + 'Points'] || [];

      let updatedList;
      if (alreadySelected) {
        setDotData(prev => {
          const copy = { ...prev };
          delete copy[dotId];
          return copy;
        });
        updatedList = currentList.filter(p => !matchesPointId(p, formattedId));
      } else {
        setDotData(prev => ({
          ...prev,
          [dotId]: { type: symptomType }
        }));
        const newEntry = selectedValue === "Retraction of Nipple" ? { id: formattedId } : { id: formattedId };
        updatedList = [...currentList.filter(p => !matchesPointId(p, formattedId)), newEntry];
      }

      const altKeyMap = {
        "lumpPoints": "lumpPoints",
        "ulcerPoints": "ulcerPoints",
        "nplRtctnPoints": "nippleRetractionPoints",
        "npldischrgPoints": "nippleDischargePoints",
        "dmplngPoints": "dimplingPoints",
        "retrctnSknPoints": "retractionSkinPoints"
      };
      const altKey = altKeyMap[pointTypeKey];

      const updatedPointsObj = {
        ...currentPoints,
        [pointTypeKey]: updatedList,
        ...(altKey ? { [altKey]: updatedList } : {})
      };

      const hasLump = (updatedPointsObj.lumpPoints?.length > 0) ? true : null;
      const hasUlcer = (updatedPointsObj.ulcerPoints?.length > 0) ? true : null;
      const hasNplRtctn = ((updatedPointsObj.nplRtctnPoints || updatedPointsObj.nippleRetractionPoints)?.length > 0) ? true : null;
      const hasNplDischrg = ((updatedPointsObj.npldischrgPoints || updatedPointsObj.nippleDischargePoints)?.length > 0) ? true : null;
      const hasDmplng = ((updatedPointsObj.dmplngPoints || updatedPointsObj.dimplingPoints)?.length > 0) ? true : null;
      const hasSknRetrctn = ((updatedPointsObj.retrctnSknPoints || updatedPointsObj.retractionSkinPoints)?.length > 0) ? true : null;

      onChange({
        ...data,
        lump: hasLump,
        ulcrtn: hasUlcer,
        niplRetrctn: hasNplRtctn,
        dschrge: hasNplDischrg,
        dmplng: hasDmplng,
        sknRetrctn: hasSknRetrctn,
        brstExmLctnsPnts: updatedPointsObj,
        examLocationPoints: updatedPointsObj
      });
    }
  };

  const renderHoverTooltip = (dotId, findings) => {
    if (!findings || findings.length === 0) return null;

    return (
      <div
        style={{
          position: 'absolute',
          bottom: 'calc(100% + 8px)',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#e2e8f0',
          border: '1px solid #cbd5e1',
          borderRadius: '6px',
          padding: '5px 9px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#1e293b',
          whiteSpace: 'normal',
          width: 'max-content',
          maxWidth: '220px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          pointerEvents: 'auto',
          textAlign: 'left',
          lineHeight: 1.3
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {findings.map((f, idx) => {
          const details = [];
          if (f.type === 'nippleDischarge') {
            const dType = f.attributes?.Discharge || f.attributes?.dischargeType || f.attributes?.selectedOption1 || '';
            const dOther = f.attributes?.OtherDisc || f.attributes?.dischargeOther || f.attributes?.textValue || '';
            if (dType) details.push(dType === 'Other' && dOther ? `Other (${dOther})` : dType);
          } else {
            if (f.attributes?.consistency || f.attributes?.selectedOption1) details.push(f.attributes?.consistency || f.attributes?.selectedOption1);
            if (f.attributes?.fixity || f.attributes?.selectedOption2) details.push(f.attributes?.fixity || f.attributes?.selectedOption2);
            if (f.attributes?.size || f.attributes?.textValue) details.push(`${f.attributes?.size || f.attributes?.textValue} mm`);
          }
          const detailsText = details.join(', ');

          return (
            <div key={idx} style={{ marginBottom: idx < findings.length - 1 ? '4px' : '0' }}>
              <span style={{ fontWeight: 700, color: '#1e293b' }}>{f.label}</span>
              {detailsText && <span style={{ fontWeight: 500, color: '#475569' }}>: {detailsText}</span>}
            </div>
          );
        })}
        <div
          style={{
            position: 'absolute',
            bottom: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid #e2e8f0'
          }}
        />
      </div>
    );
  };

  const renderDots = (side) => {
    const isCenterOnlySymptom = selectedValue === "Nipple Discharge" || selectedValue === "Retraction of Nipple";
    return Array.from({ length: 12 }, (_, i) => {
      const dotId = `${side}-dot${i + 1}`;
      const findings = getDotFindings(dotId, selectedValue);
      const isSelected = findings.length > 0;
      const isHovered = hoveredDot === dotId;

      return (
        <div
          key={dotId}
          className={`${isSelected ? `dot dot${i + 1} selected` : `dot dot${i + 1}`}`}
          style={{
            pointerEvents: isCenterOnlySymptom ? 'none' : 'auto',
            opacity: isCenterOnlySymptom ? 0.5 : 1,
            cursor: isCenterOnlySymptom ? 'not-allowed' : 'pointer'
          }}
          onMouseEnter={() => !isCenterOnlySymptom && setHoveredDot(dotId)}
          onMouseLeave={() => !isCenterOnlySymptom && setHoveredDot(null)}
          onClick={(e) => handleDotClick(dotId, e)}
        >
          {i + 1}
          {!isCenterOnlySymptom && isHovered && findings.length > 0 && renderHoverTooltip(dotId, findings)}
        </div>
      );
    });
  };

  const handleConfirmNo = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmYes = () => {
    setOpenConfirmDialog(false);
    setDotData({});
    onChange({
      ...data,
      noAbnormalityFound: true,
      shapeSizeChangeInBreast: false,
      brstChg: false,
      nippleLevelNotSame: false,
      level: false,
      skinEdema: false,
      isLessSkinEdma: false,
      isMoreSkinEdma: false,
      lymphadenopathy: false,
      lymphdnpthy: false,
      axillary: false,
      axilry: false,
      supraclavicular: false,
      supra: false,
      infraclavicular: false,
      infra: false,
      nodeAxillary: "",
      ndeAux: "",
      nodeSupraclavicular: "",
      ndSprsclvclr: "",
      lump: null,
      ulcrtn: null,
      niplRetrctn: null,
      dschrge: null,
      dmplng: null,
      sknRetrctn: null,
      brstExmLctnsPnts: {
        lumpPoints: [],
        ulcerPoints: [],
        nplRtctnPoints: [],
        npldischrgPoints: [],
        dmplngPoints: [],
        retrctnSknPoints: []
      },
      examLocationPoints: {
        lumpPoints: [],
        ulcerPoints: [],
        nippleRetractionPoints: [],
        nippleDischargePoints: [],
        dimplingPoints: [],
        retractionSkinPoints: []
      }
    });
  };

  const handleResetFindings = () => {
    setSelectedValue('');
    setDotData({});
    onChange({
      ...data,
      lump: null,
      ulcrtn: null,
      niplRetrctn: null,
      dschrge: null,
      dmplng: null,
      sknRetrctn: null,
      brstExmLctnsPnts: {
        lumpPoints: [],
        ulcerPoints: [],
        nplRtctnPoints: [],
        npldischrgPoints: [],
        dmplngPoints: [],
        retrctnSknPoints: []
      },
      examLocationPoints: {
        lumpPoints: [],
        ulcerPoints: [],
        nippleRetractionPoints: [],
        nippleDischargePoints: [],
        dimplingPoints: [],
        retractionSkinPoints: []
      }
    });
  };

  const handletopRadioChange = (event) => {
    const { id } = event.target;
    if (selectedValue === id) {
      setSelectedValue('');
    } else {
      setSelectedValue(id);
    }
  };

  const handleRadioChange = (event) => {
    const { name, value } = event.target;
    const boolVal = value === "true";

    const aliasMap = {
      shapeSizeChangeInBreast: ['shapeSizeChangeInBreast', 'brstChg'],
      nippleLevelNotSame: ['nippleLevelNotSame', 'level'],
      skinEdema: ['skinEdema'],
      lymphadenopathy: ['lymphadenopathy', 'lymphdnpthy'],
      axillary: ['axillary', 'axilry'],
      supraclavicular: ['supraclavicular', 'supra'],
      infraclavicular: ['infraclavicular', 'infra'],
    };

    const keysToUpdate = aliasMap[name] || [name];
    const updates = {};
    keysToUpdate.forEach(k => { updates[k] = boolVal; });

    if (name === 'skinEdema' && !boolVal) {
      updates.isLessSkinEdma = false;
      updates.isMoreSkinEdma = false;
    }

    if (name === 'lymphadenopathy' && !boolVal) {
      updates.axillary = false;
      updates.axilry = false;
      updates.supraclavicular = false;
      updates.supra = false;
      updates.infraclavicular = false;
      updates.infra = false;
      updates.nodeAxillary = "";
      updates.ndeAux = "";
      updates.nodeSupraclavicular = "";
      updates.ndSprsclvclr = "";
    }

    if (name === 'axillary' && !boolVal) {
      updates.nodeAxillary = "";
      updates.ndeAux = "";
    }

    if (name === 'supraclavicular' && !boolVal) {
      updates.nodeSupraclavicular = "";
      updates.ndSprsclvclr = "";
    }

    onChange({
      ...data,
      ...updates
    });
  };

  const handleSkinEdemaTypeChange = (e) => {
    const { value } = e.target;
    onChange({
      ...data,
      isLessSkinEdma: value === "less",
      isMoreSkinEdma: value === "more"
    });
  };

  const handleNodeSelectChange = (fieldName, optionsList, newValues) => {
    let selectedValues = typeof newValues === 'string' ? newValues.split(',') : newValues;

    const selectAllToken = optionsList.find(opt => opt.value === 'Select All' || opt.value === 'All')?.value || 'Select All';

    if (selectedValues.includes(selectAllToken)) {
      const realOptions = optionsList.map(opt => opt.value).filter(v => v !== selectAllToken);
      const currentlySelectedReal = selectedValues.filter(v => v !== selectAllToken);

      if (currentlySelectedReal.length === realOptions.length) {
        selectedValues = [];
      } else {
        selectedValues = realOptions;
      }
    }

    const strVal = selectedValues.join(',');

    const aliasMap = {
      nodeAxillary: ['nodeAxillary', 'ndeAux'],
      nodeSupraclavicular: ['nodeSupraclavicular', 'ndSprsclvclr']
    };

    const keysToUpdate = aliasMap[fieldName] || [fieldName];
    const updates = {};
    keysToUpdate.forEach(k => { updates[k] = strVal; });

    onChange({
      ...data,
      ...updates
    });
  };

  const shapeSizeChangeVal = data?.shapeSizeChangeInBreast ?? data?.brstChg;
  const nippleLevelNotSameVal = data?.nippleLevelNotSame ?? data?.level;
  const skinEdemaVal = data?.skinEdema;
  const lymphadenopathyVal = data?.lymphadenopathy ?? data?.lymphdnpthy;
  const axillaryVal = data?.axillary ?? data?.axilry;
  const supraclavicularVal = data?.supraclavicular ?? data?.supra;
  const infraclavicularVal = data?.infraclavicular ?? data?.infra;
  const nodeAxillaryVal = data?.nodeAxillary ?? data?.ndeAux ?? "";
  const nodeSupraclavicularVal = data?.nodeSupraclavicular ?? data?.ndSprsclvclr ?? "";

  return (
    <>
      <div className="row">
        <Box sx={{ display: 'flex', width: '100%', mb: 3, px: 2, justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155', fontSize: '16px' }}>
              Examination
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={noAbnormalityFound}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setOpenConfirmDialog(true);
                    } else {
                      onChange({
                        ...data,
                        noAbnormalityFound: false
                      });
                    }
                  }}
                  sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#3f51b5' } }}
                />
              }
              label={<Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>No Abnormality Detected</Typography>}
            />
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              label="Assessment Date"
              value={assesDate ? (dayjs(assesDate, 'YYYY-MM-DD').isValid() ? dayjs(assesDate, 'YYYY-MM-DD') : dayjs(assesDate, 'DD-MM-YYYY')) : null}
              onChange={(newValue) => onChange({
                ...data,
                assesDate: newValue ? newValue.format('DD-MM-YYYY') : '',
                assessmentDate: newValue ? newValue.format('YYYY-MM-DD') : ''
              })}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: '220px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }
                }
              }}
            />
          </LocalizationProvider>
        </Box>

        {[
          { id: "Lump", label: "LUMP", img: "/images/breast_lump.png" },
          { id: "Ulcer", label: "Ulcer", img: "/images/breast_ulcer.png" },
          { id: "Retraction of Nipple", label: "RETRACTION OF NIPPLE", img: "/images/breast_erosion.png" },
          { id: "Nipple Discharge", label: "NIPPLE DISCHARGE", img: "/images/breast_discharge.png" },
          { id: "Skin Dimpling Puckering", label: "SKIN DIMPLING / PUCKERING", img: "/images/breast_erosion.png" },
          { id: "Retraction of Skin", label: "RETRACTION OF SKIN", img: "/images/breast_erosion.png" },
        ].map(({ id, label, img }) => {
          const isSelected = selectedValue === id;
          return (
            <Box key={id} className="col-md-4 col-xs-6 col-6" sx={{ mb: 2 }}>
              <FormControlLabel
                className={`oralRadio d-flex align-items-center justify-content-center luco-lable-text mb-1 ${isSelected ? 'selected' : ''}`}
                sx={{
                  border: isSelected ? '2px solid #5c6bc0' : '1px solid #cfd8dc',
                  backgroundColor: isSelected ? '#9fa8da' : '#ffffff',
                  color: isSelected ? '#1c2d5a' : '#2c3e50',
                  borderRadius: '6px',
                  m: 0,
                  p: '8px 12px',
                  width: '100%',
                  cursor: noAbnormalityFound ? 'not-allowed' : 'pointer',
                  pointerEvents: noAbnormalityFound ? 'none' : 'auto',
                  opacity: noAbnormalityFound ? 0.6 : 1,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    border: isSelected ? '2px solid #5c6bc0' : '1px solid #5c6bc0',
                    backgroundColor: isSelected ? '#9fa8da' : '#f5f7fa',
                  },
                  '& .MuiTypography-root': {
                    fontWeight: 700,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
                control={
                  <Radio
                    name="breastScreeningRadio"
                    id={id}
                    onChange={handletopRadioChange}
                    checked={isSelected}
                    disabled={noAbnormalityFound}
                    sx={{ display: 'none' }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: '#cfd8dc',
                        border: '2px solid #ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '12px',
                        flexShrink: 0,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <img
                        src={img}
                        alt=""
                        style={{
                          width: 20,
                          height: 20,
                          objectFit: 'contain'
                        }}
                      />
                    </Box>
                    <span style={{ fontSize: '13px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
                  </Box>
                }
              />
            </Box>
          );
        })}
      </div>

      <div className="demoTaskSection">
        <div className="breastBoxSection">
          <div className="mouthOralLocHeader">
            <h6>Showing earlier findings</h6>
            <div className="rightFinding">
              <div className="ShowAllFindings" onClick={() => setSelectedValue("")} style={{ cursor: 'pointer' }}>
                Show All Findings
              </div>
              <div className="reset" onClick={handleResetFindings} style={{ cursor: 'pointer' }}>
                Reset
              </div>
            </div>
          </div>
          <h5>Mark Location for : {selectedValue || "All Findings"}</h5>
          <div className="breastCanserCheck" style={{
            pointerEvents: noAbnormalityFound ? 'none' : 'auto',
            opacity: noAbnormalityFound ? 0.6 : 1
          }}>
            <div className="leftBreast">
              <h5>Right</h5>
              <div className="wrapper">
                <div className="ellipse" />
                <div className="line vertical" />
                <div className="line horizontal" />
                {renderDots("left")}
                {(() => {
                  const findings = getDotFindings("left-center", selectedValue);
                  const isSelected = findings.length > 0;
                  return (
                    <div
                      className={`dot center-dot ${isSelected ? "selected" : ""}`}
                      onMouseEnter={() => setHoveredDot("left-center")}
                      onMouseLeave={() => setHoveredDot(null)}
                      onClick={(e) => handleDotClick("left-center", e)}
                    >
                      {hoveredDot === "left-center" && findings.length > 0 && renderHoverTooltip("left-center", findings)}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="rightBreast">
              <h5>Left</h5>
              <div className="wrapper">
                <div className="ellipse" />
                <div className="line vertical" />
                <div className="line horizontal" />
                {renderDots("right")}
                {(() => {
                  const findings = getDotFindings("right-center", selectedValue);
                  const isSelected = findings.length > 0;
                  return (
                    <div
                      className={`dot center-dot ${isSelected ? "selected" : ""}`}
                      onMouseEnter={() => setHoveredDot("right-center")}
                      onMouseLeave={() => setHoveredDot(null)}
                      onClick={(e) => handleDotClick("right-center", e)}
                    >
                      {hoveredDot === "right-center" && findings.length > 0 && renderHoverTooltip("right-center", findings)}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          {activeDot && (
            <DotDropdown
              dotId={activeDot}
              position={dropdownPos}
              selectedValue={selectedValue}
              onClose={() => setActiveDot(null)}
              dotData={dotData[activeDot] || (getDotFindings(activeDot, selectedValue)[0]?.attributes)}
            />
          )}
        </div>

        <div className="breastFormSection" style={{ padding: '20px', width: '100%', pointerEvents: noAbnormalityFound ? 'none' : 'auto', opacity: noAbnormalityFound ? 0.6 : 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" disabled={noAbnormalityFound}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#5f6368' }}>Change in shape / size of the breast:</Typography>
                <RadioGroup
                  row
                  name="shapeSizeChangeInBreast"
                  value={shapeSizeChangeVal === true ? "true" : shapeSizeChangeVal === false ? "false" : ""}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" disabled={noAbnormalityFound}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#5f6368' }}>Both nipples not at same level:</Typography>
                <RadioGroup
                  row
                  name="nippleLevelNotSame"
                  value={nippleLevelNotSameVal === true ? "true" : nippleLevelNotSameVal === false ? "false" : ""}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" disabled={noAbnormalityFound}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#5f6368' }}>Skin Edema:</Typography>
                <RadioGroup
                  row
                  name="skinEdema"
                  value={skinEdemaVal === true ? "true" : skinEdemaVal === false ? "false" : ""}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" disabled={noAbnormalityFound || !skinEdemaVal}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#5f6368' }}>Skin Edema Type:</Typography>
                <RadioGroup
                  row
                  name="isLessSkinEdma"
                  value={data?.isLessSkinEdma === true ? "less" : data?.isMoreSkinEdma === true ? "more" : ""}
                  onChange={handleSkinEdemaTypeChange}
                >
                  <FormControlLabel value="less" control={<Radio size="small" />} label="Less than 1/3rd of area of breast" />
                  <FormControlLabel value="more" control={<Radio size="small" />} label="More than 1/3rd of area of breast" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" disabled={noAbnormalityFound}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#5f6368' }}>Lymphadenopathy:</Typography>
                <RadioGroup
                  row
                  name="lymphadenopathy"
                  value={lymphadenopathyVal === true ? "true" : lymphadenopathyVal === false ? "false" : ""}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" disabled={noAbnormalityFound || !lymphadenopathyVal}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#5f6368' }}>Axillary:</Typography>
                <RadioGroup
                  row
                  name="axillary"
                  value={axillaryVal === true ? "true" : axillaryVal === false ? "false" : ""}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" disabled={noAbnormalityFound || !lymphadenopathyVal}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#5f6368' }}>Supraclavicular:</Typography>
                <RadioGroup
                  row
                  name="supraclavicular"
                  value={supraclavicularVal === true ? "true" : supraclavicularVal === false ? "false" : ""}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl component="fieldset" disabled={noAbnormalityFound || !lymphadenopathyVal}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#5f6368' }}>Infraclavicular:</Typography>
                <RadioGroup
                  row
                  name="infraclavicular"
                  value={infraclavicularVal === true ? "true" : infraclavicularVal === false ? "false" : ""}
                  onChange={handleRadioChange}
                >
                  <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#5f6368' }}>Axillary Node Details</Typography>
              <FormControl fullWidth variant="standard" disabled={noAbnormalityFound || !lymphadenopathyVal || !axillaryVal}>
                <MuiSelect
                  multiple
                  value={nodeAxillaryVal ? nodeAxillaryVal.split(',').filter(Boolean) : []}
                  onChange={(e) => handleNodeSelectChange('nodeAxillary', axillaryOptions, e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {axillaryOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox checked={nodeAxillaryVal ? nodeAxillaryVal.split(',').includes(opt.value) : false} />
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#5f6368' }}>Supraclavicular Node Details</Typography>
              <FormControl fullWidth variant="standard" disabled={noAbnormalityFound || !lymphadenopathyVal || !supraclavicularVal}>
                <MuiSelect
                  multiple
                  value={nodeSupraclavicularVal ? nodeSupraclavicularVal.split(',').filter(Boolean) : []}
                  onChange={(e) => handleNodeSelectChange('nodeSupraclavicular', supraclavicularOptions, e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {supraclavicularOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox checked={nodeSupraclavicularVal ? nodeSupraclavicularVal.split(',').includes(opt.value) : false} />
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Grid>
          </Grid>
        </div>
      </div>

      <Dialog
        open={openConfirmDialog}
        onClose={handleConfirmNo}
        PaperProps={{
          sx: {
            padding: '16px',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '100%',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px', p: 1, pb: 2 }}>Confirm</DialogTitle>
        <DialogContent sx={{ p: 1, pb: 2 }}>
          <Typography sx={{ fontSize: '15px', color: '#333', lineHeight: 1.5 }}>
            Abnormal findings, if any, will be cleared and the patient will be marked NAD. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={handleConfirmNo}
            variant="contained"
            sx={{
              backgroundColor: '#3f51b5',
              color: '#fff',
              fontWeight: 'bold',
              minWidth: '80px',
              '&:hover': {
                backgroundColor: '#303f9f',
              }
            }}
          >
            NO
          </Button>
          <Button
            onClick={handleConfirmYes}
            variant="contained"
            sx={{
              backgroundColor: '#3f51b5',
              color: '#fff',
              fontWeight: 'bold',
              minWidth: '80px',
              '&:hover': {
                backgroundColor: '#303f9f',
              }
            }}
          >
            YES
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
